import { toPayloadEventId } from "./eventRecordId"

const POST_LEVEL_KEYS = new Set([
  "contentPostStatusId",
  "socialNetworkTypeId",
  "postText",
  "locationId",
  "location",
  "contentPostTypeId",
  "postDatetime",
  "mediaAssetIds",
  "brandToneScore",
  "mediaUserTags",
])

const TOPIC_NULLABLE_KEYS = new Set(["eventId", "event"])

function coerceOptionalNumber(value) {
  if (value == null || value === "") return value
  const n = Number(value)
  return Number.isNaN(n) ? value : n
}

function normalizePostField(key, value) {
  if (value == null) return value
  if (key === "locationId") return coerceOptionalNumber(value)
  if (
    key === "socialNetworkTypeId" ||
    key === "contentPostTypeId" ||
    key === "contentPostStatusId"
  ) {
    return coerceOptionalNumber(value)
  }
  return value
}

const TOPIC_PATCH_FIELD_KEYS = ["eventId", "event", "contentPillarId", "topicName", "name"]

/**
 * PATCH /posts/:id requires `posts[]` even when only topic fields (e.g. eventId) change.
 * Merges current post snapshot fields when the change set is topic-only.
 */
export function ensureTopicPatchHasPostBody(flatChanges = {}, snapshot = {}) {
  if (!flatChanges || typeof flatChanges !== "object") return flatChanges

  const hasTopicField = TOPIC_PATCH_FIELD_KEYS.some((k) => flatChanges[k] !== undefined)
  const hasPostField = Object.keys(flatChanges).some((k) => POST_LEVEL_KEYS.has(k))

  if (!hasTopicField || hasPostField) return flatChanges

  const merged = { ...flatChanges }
  if (snapshot.postDatetime != null && merged.postDatetime === undefined) {
    merged.postDatetime = snapshot.postDatetime
  }
  if (
    merged.mediaUserTags === undefined &&
    Array.isArray(snapshot.mediaUserTags) &&
    snapshot.mediaUserTags.length > 0
  ) {
    merged.mediaUserTags = snapshot.mediaUserTags
  }
  if (merged.postText === undefined && snapshot.postText != null && snapshot.postText !== "") {
    merged.postText = snapshot.postText
  }
  if (merged.mediaAssetIds === undefined && Array.isArray(snapshot.mediaAssetIds) && snapshot.mediaAssetIds.length > 0) {
    merged.mediaAssetIds = snapshot.mediaAssetIds
  }

  return merged
}

/**
 * PATCH /posts/:id — topic fields at root, post fields nested under `posts[0]`.
 * Accepts legacy flat payloads from review panel / campaign drawer (`topicName` → `name`).
 */
export function buildPostsApiPatchPayload(flatPayload = {}) {
  const topic = {}
  const post = {}

  for (const [key, value] of Object.entries(flatPayload)) {
    if (value === undefined) continue

    if (key === "topicName" || key === "name") {
      if (value != null && value !== "") topic.name = value
      continue
    }

    if (key === "contentPillarId") {
      if (value != null && value !== "") {
        const n = Number(value)
        if (!Number.isNaN(n)) topic.contentPillarId = n
      }
      continue
    }

    if (TOPIC_NULLABLE_KEYS.has(key)) {
      if (key === "eventId") {
        if (value === null || value === 0 || value === "0") {
          topic.eventId = 0
        } else {
          const normalized = toPayloadEventId(value)
          if (normalized) topic.eventId = normalized
        }
      } else {
        topic[key] = value
      }
      continue
    }

    if (POST_LEVEL_KEYS.has(key)) {
      post[key] = normalizePostField(key, value)
    }
  }

  const out = { ...topic }
  if (Object.keys(post).length > 0) {
    out.posts = [post]
  }
  return out
}
